import { Box, Paper, Typography } from '@mui/material';
import { PageContainer } from '@toolpad/core/PageContainer';
import type React from 'react';

const UserGuidePage: React.FC = () => {
  return (
    <PageContainer title="User Guide">
      <Paper sx={{ p: 3, mt: 0 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Introduction
          </Typography>
          <Typography paragraph>
            Welcome to BIDLOGIQ! This guide will walk you through the main features of the
            application to help you get started with managing your tenders effectively.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            My Tenders Dashboard
          </Typography>
          <Typography paragraph>
            The dashboard is your central hub for all tenders you are actively working on. Here you
            can see a list of your tenders, their current status, and key deadlines. You can add new
            tenders, archive old ones, and quickly access the details of each tender. After opening
            the details for a tender, you can add supporting documents, regenerate reports and
            drafts, and upload your latest proposal for review.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Creating a Manual Tender
          </Typography>
          <Typography paragraph>
            For situations where you don't have a formal RFT document—such as a request via email or
            a verbal agreement—you can create a tender manually. This feature allows you to input
            all the necessary details into a structured form to kickstart the AI analysis and
            response generation process. Entering specific details will help the AI generate a more
            accurate and tailored response, while leaving some details blank will allow the AI to
            use its own knowledge and experience to fill in the gaps.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            How to Use the Manual Tender Form:
          </Typography>
          <ul>
            <li>
              <Typography component="span">
                <strong>1. Project Details:</strong> Enter the fundamental details about the
                project, such as the title, client information, and any internal reference numbers.
              </Typography>
            </li>
            <li>
              <Typography component="span">
                <strong>2. The 'Why' - Context & Objectives:</strong> Provide background information
                on the project and list the key objectives the client wants to achieve. This helps
                the AI understand the client's motivations.
              </Typography>
            </li>
            <li>
              <Typography component="span">
                <strong>3. The 'What' - Scope & Deliverables:</strong> Detail the specific work to
                be done and the tangible outcomes or items you will provide to the client.
              </Typography>
            </li>
            <li>
              <Typography component="span">
                <strong>4. The 'How' - Key Parameters & Constraints:</strong> Define the operational
                and commercial aspects, including your proposed fee structure, budget estimates,
                project duration, and the client's sensitivity to price. A deadline for the
                submission should also be included here.
              </Typography>
            </li>
            <li>
              <Typography component="span">
                <strong>5. Foundational Tender Documents:</strong> Upload relevant RFT files, scope
                briefs, and direct client correspondence to establish context for the current
                tender.
                <em>
                  {' '}
                  Warning: Do not upload past projects, delivery files, or unrelated personal
                  documents here.
                </em>
              </Typography>
            </li>
          </ul>
          <Typography paragraph sx={{ mt: 2 }}>
            Once you have filled out the form, you can either download a CSV copy for internal
            review or click "Create Manual Tender" to submit it to the system for processing.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Public Tenders
          </Typography>
          <Typography paragraph>
            The Public Tenders list is where you can find public tenders available for you to bid
            on. You can search, filter, and browse through a comprehensive list of opportunities.
            When you find a tender you're interested in, you can add it to your dashboard to begin
            working on it.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Template Management
          </Typography>
          <Typography paragraph>
            The Template Management page allows you to upload and manage your own branded Microsoft
            Word templates for Draft Responses, CVs, and Case Studies. This ensures that all
            AI-generated content is exported directly into your organization's specific format and
            branding.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            How to Create and Upload a Template:
          </Typography>
          <Typography component="div">
            <ol>
              <li>
                <strong>Start with a Document:</strong> You can download one of our example
                templates or use your existing company Word document (.docx).
              </li>
              <li>
                <strong>Insert Merge Fields:</strong> On the Template Management page, open the{' '}
                <strong>Merge Fields</strong> sidebar (or drawer on smaller screens). Copy the
                relevant tags (e.g., <code>{'{{content}}'}</code>, <code>{'{{person_name}}'}</code>)
                and paste them into your Word document where you want the dynamic content to appear.
              </li>
              <li>
                <strong>Upload & Verify:</strong> Upload your file to the system. BIDLOGIQ will
                automatically scan the document to ensure all required tags are present.
              </li>
              <li>
                <strong>Set Defaults:</strong> Once uploaded, you can set a template as your{' '}
                <strong>Personal Default</strong> (only for you) or <strong>Team Default</strong>{' '}
                (for all users in your organization).
              </li>
            </ol>
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            Template Types:
          </Typography>
          <ul>
            <li>
              <Typography component="span">
                <strong>Response Drafts:</strong> The main proposal document structure.
              </Typography>
            </li>
            <li>
              <Typography component="span">
                <strong>CV Templates:</strong> Formats for team member resumes.
              </Typography>
            </li>
            <li>
              <Typography component="span">
                <strong>Case Study Templates:</strong> Layouts for project case studies.
              </Typography>
            </li>
          </ul>
          <Typography paragraph sx={{ mt: 2, fontStyle: 'italic' }}>
            <strong>Need help?</strong> We offer free 1:1 support to help you integrate your
            branding. Contact support@bidlogiq.com for assistance.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Knowledge Base
          </Typography>
          <Typography paragraph>
            The Knowledge Base is a repository for all your important documents. You can upload,
            organize, and manage files such as past proposals, reports, and CVs that will be used to
            generate your tender responses.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            SharePoint Integration
          </Typography>
          <Typography paragraph>
            Connect your organization's SharePoint to maintain control of your data. When connected,
            any file you upload to BIDLOGIQ (library documents, tender drafts, etc.) is physically
            stored in your own SharePoint environment instead of BIDLOGIQ's cloud storage.
          </Typography>
          <Typography component="div">
            <ol>
              <li>
                Go to the <strong>My Account</strong> page.
              </li>
              <li>
                Click <strong>Connect SharePoint</strong> and follow the prompts to authorize
                access.
              </li>
            </ol>
          </Typography>
          <Typography paragraph>
            <strong>Note:</strong> You will still manage your files (uploading, organizing,
            deleting) entirely within the BIDLOGIQ platform. The integration simply ensures the
            underlying files reside securely in your own tenant.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Statistics
          </Typography>
          <Typography paragraph>
            This page provides you with analytics on your tendering activity and subscription usage.
            You can view statistics on your win/loss rate, tender submission frequency, and other
            key performance indicators to help you refine your bidding strategy.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Company Info
          </Typography>
          <Typography paragraph>
            Here you can manage your company's details. It's important to keep your company
            information up to date to ensure all generated tender responses are accurate.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Subscriptions and Usage Limits
          </Typography>
          <Typography paragraph>
            BIDLOGIQ offers different subscription tiers, each with specific usage limits to cater
            to your business needs. These limits are typically reset on a monthly basis, depending
            on your subscription level. You can view your current subscription level and usage in
            the 'Statistics' section. For more details on the available tiers, please visit our
            subscriptions page.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            Monthly Limit Definitions:
          </Typography>
          <ul>
            <li>
              <Typography component="span">
                <strong>Tenders Added to Dashboard:</strong> The total number of new tenders you can
                add to your 'My Tenders' dashboard each month, from either manual uploads or the
                Public Tenders list.
              </Typography>
            </li>
            <li>
              <Typography component="span">
                <strong>Capability Reports:</strong> The number of AI-generated capability reports
                you can create per month.
              </Typography>
            </li>
            <li>
              <Typography component="span">
                <strong>Total Draft Responses:</strong> The combined total number of draft tender
                responses you can generate across all your active tenders per month.
              </Typography>
            </li>
            <li>
              <Typography component="span">
                <strong>Drafts per Tender:</strong> The maximum number of draft iterations you can
                generate for a single tender each month.
              </Typography>
            </li>
            <li>
              <Typography component="span">
                <strong>Total AI Reviews:</strong> The total number of AI-powered reviews you can
                request on your drafts across all tenders per month.
              </Typography>
            </li>
            <li>
              <Typography component="span">
                <strong>Reviews per Tender:</strong> The maximum number of AI reviews you can
                request for a single tender each month.
              </Typography>
            </li>
            <li>
              <Typography component="span">
                <strong>Total Additional Documents:</strong> The total number of additional (e.g.,
                filled forms, pricing schedules, CVs) you can generate across all tenders per month.
                Note that this does not include CVs generated alongside the draft, i.e., from your
                document library or supporting docs.
              </Typography>
            </li>
          </ul>
        </Box>

        {/* <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Glossary
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            Tender
          </Typography>
          <Typography paragraph>
            A formal offer to perform work or supply goods at a stated price. In BIDLOGIQ, a
            "Tender" object represents a single bidding opportunity you are tracking or
            participating in.
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            Draft
          </Typography>
          <Typography paragraph>
            A tender that you have started working on but have not yet submitted. Drafts are saved
            on your dashboard.
          </Typography>
        </Box> */}
      </Paper>
    </PageContainer>
  );
};

export default UserGuidePage;
